import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import ResizeTextBox from '@src/components/ResizeTextBox';
import { delay } from '@src/utils/delay';
import { splitParagraphIntoSentence } from '@src/services/sentence';
import actions from '@src/redux/actions';
import { MAX_LENGTH_TITLE } from '@src/constants/request';
import {
  LOCAL_STORAGE_EXPIRES_TIME,
  SET_TIME_AUTO_SAVE,
} from '@src/constants/tts';
import {
  setDataLocalStorage,
  getDataLocalStorage,
  deleteDataLocalStorage,
} from '@src/utils/localStorage';
import debounce from '@src/utils/debounce';
import { calculateElapsedDateTime } from '@src/utils/date';
import useElementWidth from '@src/hooks/useElementWidth';
import { MAX_TITLE_LENGTH } from '@src/configs';
import { isEmpty, isEqual } from 'lodash';
import ROUTES from '@src/constants/route';
import NewPackageInfoV3 from '@src/containers/Layout/SidebarNew/NewPackageInfoV3';
import { Box, useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
import { BREAK_TIME_REGEX, REGEX } from '@src/constants/voice';
import { getTTSTourGuideValue } from '@src/utils/tourGuide';
import NewActionsHeader from '@src/containers/Layout/NewActionsHeader';
import useCustomerSupportView from '@src/hooks/useCustomerSupportView';
import { NEW_ACTIONS_HEADER_MENU } from '@src/constants';
import StatusSaving from '../TitleBar/StatusSaving';
import { StyledTitleProduct } from './index.style';

const SAVE_TITLE_WIDTH = 100;
const TITLE_MARGIN_RIGHT = 20;

const TitleBarNew = ({
  isNewLayout,
  loading,
  inputLength = 0,
  isLoadDraft,
  onLoadEditContent,
  onChangeInputLength,
  onOpenConfirmDialog,
  content,
  enableEditor,
  onChangeRequestLoading,
  onEnableEditor,
}) => {
  const history = useHistory();
  const { isCustomerSupportView } = useCustomerSupportView();
  const {
    relateDataRequest,
    synthesisRequest,
    openSentences,
    voice,
    advanceFeature,
    sentences,
    paragraphs,
  } = useSelector((state) => state.synthesisRequest);
  const user = useSelector((state) => state.auth.user);
  const hasConvertTts = !loading;
  const [groupLoading, setGroupLoading] = useState(false);
  const requestInfoRef = useRef();
  const requestInfoWidth = useElementWidth(requestInfoRef);
  const titleRef = useRef();
  const titleWidth = useElementWidth(titleRef);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const routerPath = useLocation();
  const [isOpenDialog, setIsOpenDialog] = useState(true);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isShowUp, setIsShowUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChangeShowUp = () => {
    if (!isSaving) setIsShowUp(false);
  };

  const handleValidateTTSTitle = (value) => {
    if (value.length <= MAX_LENGTH_TITLE) return true;
    dispatch(
      actions.noti.push({
        severity: 'warning',
        message: 'updateTitleMaxLength',
        value: MAX_LENGTH_TITLE,
      }),
    );
    return false;
  };

  const renderPackageInfo = () => <NewPackageInfoV3 />;

  const userPackageInfo = () =>
    !isMobile ? (
      <div className="wallet">
        <Box className="wallet-character">{renderPackageInfo()}</Box>
      </div>
    ) : (
      <></>
    );

  const updateRequestStatus = () => {
    if (!enableEditor) {
      onEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest());
    }
  };

  const handleChangeTTSTitle = async ({ newTitle }) => {
    if (!synthesisRequest.id) {
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest('title', newTitle),
      );
      return;
    }
    try {
      if (newTitle === synthesisRequest.title) return;

      if (newTitle.length > MAX_LENGTH_TITLE) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'updateTitleMaxLength',
            value: MAX_LENGTH_TITLE,
          }),
        );
        return;
      }
      if (!enableEditor) updateRequestStatus();
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest('title', newTitle),
      );
      onChangeRequestLoading(true);
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: error?.response?.data?.error_message || 'updateFailure',
        }),
      );
    }
  };

  const handleSaveDraftOld = () => {
    if (isOpenDialog && !inputLength) return;
    let sentencesSave;
    if (advanceFeature) sentencesSave = paragraphs;

    if (!advanceFeature) {
      if (openSentences) {
        sentencesSave = sentences;
      } else {
        sentencesSave = splitParagraphIntoSentence(
          content.getCurrentContent().getPlainText('\n'),
          voice,
        );
      }
    }
    const dataDraft = {
      advanceFeature,
      openSentences,
      inputLength,
      synthesisRequest,
      sentencesSave,
      voice,
      enableEditor,
    };
    setDataLocalStorage('dataDraft', dataDraft, LOCAL_STORAGE_EXPIRES_TIME);
    if (
      !isEmpty(relateDataRequest) &&
      !isEqual(relateDataRequest, getDataLocalStorage('relatedRequest') || {})
    ) {
      setDataLocalStorage(
        'relatedRequest',
        relateDataRequest,
        LOCAL_STORAGE_EXPIRES_TIME,
      );
    }

    setIsSaving(false);
  };

  const processContentDraft = () => {
    if (inputLength) handleSaveDraftOld();

    if (!isOpenDialog && !inputLength) {
      setIsSaving(false);
      deleteDataLocalStorage('dataDraft');
    }
  };

  const handleLoadDraft = () => {
    const data = getDataLocalStorage('dataDraft');
    if (!data) return;

    // Switch opensentences types
    dispatch(actions.synthesisRequest.updateOpenSentences(data.openSentences));

    // Load Voice Type
    dispatch(actions.synthesisRequest.updateVoice(data.voice));

    // Load inputLengh
    onChangeInputLength(data.inputLength);
    // Load setting of voice type
    const synthesisRequestSave = data.synthesisRequest;
    Object.keys(synthesisRequestSave).map((key) =>
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest(
          key,
          synthesisRequestSave[key],
        ),
      ),
    );
    // Load content  edit save
    onLoadEditContent(
      data.sentencesSave,
      data.advanceFeature,
      data.openSentences,
      data.enableEditor,
    );
  };

  const getTitle = (plainText, voiceLanguage) => {
    const SPACE = ' ';
    const CHINESE_CHARACTERS_LANGUAGE_CODES = ['cmn-CN', 'ja-JP', 'ko-KR'];

    if (plainText.length <= MAX_LENGTH_TITLE) {
      return plainText;
    }

    const lastSpaceIndex = plainText.lastIndexOf(SPACE, MAX_TITLE_LENGTH - 1);
    const firstSpaceIndex = plainText.indexOf(SPACE);

    if (
      firstSpaceIndex !== -1 &&
      firstSpaceIndex <= MAX_TITLE_LENGTH - 1 &&
      !CHINESE_CHARACTERS_LANGUAGE_CODES.includes(voiceLanguage)
    )
      return plainText.substring(0, lastSpaceIndex + 1);

    return plainText.substring(0, MAX_TITLE_LENGTH);
  };

  const handleClickTitle = () => {
    if (synthesisRequest.title) return;

    let sentencesSave;
    if (advanceFeature) sentencesSave = paragraphs;
    if (!advanceFeature) {
      if (openSentences) {
        sentencesSave = sentences;
      } else {
        sentencesSave = splitParagraphIntoSentence(
          content.getCurrentContent().getPlainText('\n'),
          voice,
        );
      }
    }

    const text = sentencesSave.reduce(
      (acc, sentence) => acc + sentence.text,
      ' ',
    );

    const plainText = text
      .replace(REGEX.ADVANCE_TAG, '')
      .replace(BREAK_TIME_REGEX, ' ')
      .trim();
    if (plainText) {
      const newTitle = getTitle(plainText, voice.languageCode);
      handleChangeTTSTitle({ newTitle });
    }
  };

  useEffect(() => {
    if (inputLength) {
      setIsOpenDialog(false);
      setIsSaving(true);
      setIsShowUp(true);
    }
    debounce(processContentDraft, SET_TIME_AUTO_SAVE)();
  }, [
    advanceFeature,
    openSentences,
    inputLength,
    sentences,
    voice,
    synthesisRequest,
    paragraphs,
  ]);

  useEffect(() => {
    const data = getDataLocalStorage('dataDraft');
    const doneTourGuide = getTTSTourGuideValue(user?.id);
    if (
      data &&
      user.hasAgreedToTerms &&
      doneTourGuide &&
      !isCustomerSupportView
    ) {
      onOpenConfirmDialog();
    }
    setIsOpenDialog(true);

    return () => {
      setIsOpenDialog(true);
    };
  }, [routerPath, user.hasAgreedToTerms]);

  useEffect(() => {
    if (isLoadDraft) handleLoadDraft();
  }, [isLoadDraft]);

  useEffect(() => {
    if (isSaving) {
      setIsShowUp(true);
    } else {
      debounce(handleChangeShowUp, 1000)();
    }
  }, [isSaving]);

  const renderSynthesisRequestDateTime = () => {
    if (synthesisRequest?.createdAt && !enableEditor) {
      return calculateElapsedDateTime(synthesisRequest?.createdAt, t);
    }
    return <div className="empty-title-footer" />;
  };

  const loadingUXOld = !hasConvertTts;

  useMemo(async () => {
    if (loadingUXOld) {
      setGroupLoading(true);
    }
    if (groupLoading && !loadingUXOld) {
      await delay(1000);
      setGroupLoading(false);
    }
  }, [loadingUXOld]);

  const maxWidth = isShowUp
    ? titleWidth - (requestInfoWidth || 0) - SAVE_TITLE_WIDTH
    : titleWidth - (requestInfoWidth || 0) - TITLE_MARGIN_RIGHT;
  return (
    <>
      <StyledTitleProduct isNewLayout={isNewLayout} ref={titleRef}>
        {!isNewLayout && (
          <div className="base-input">
            <ResizeTextBox
              value={synthesisRequest.title || ''}
              placeholder={t('noTitle')}
              onValidate={handleValidateTTSTitle}
              onClick={handleClickTitle}
              onChange={handleChangeTTSTitle}
              maxWidth={maxWidth}
              maxWidthxl={350}
              footerLabel={renderSynthesisRequestDateTime()}
            />
            {isShowUp && !isNewLayout && !synthesisRequest?.status && (
              <StatusSaving savingStatus={isSaving} />
            )}
          </div>
        )}
        {!isMobile && (
          <NewActionsHeader
            menuShow={[
              NEW_ACTIONS_HEADER_MENU.TASK_CREDIT,
              NEW_ACTIONS_HEADER_MENU.GUIDE_CREDIT,
              NEW_ACTIONS_HEADER_MENU.LEADERBOARD_VOICE,
              NEW_ACTIONS_HEADER_MENU.NOTIFICATION,
            ]}
            onTaskCredits={() =>
              dispatch(actions.layout.setShowOnboardingPopup(true))
            }
            onLeaderboardVoice={() => {
              history.push(ROUTES.VOICE_LEADERBOARD);
            }}
          />
        )}
        <div ref={requestInfoRef}>{userPackageInfo()}</div>
      </StyledTitleProduct>
    </>
  );
};

export default TitleBarNew;
